import type { RetryPlan } from '../interfaces/paymentInit.interface';
import type { RetryResult } from '../types/paymentInit';

type PaymentInitTask = (isCurrent: () => boolean) => Promise<void>;

function wait(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function withDeadline(task: Promise<void>, timeoutMs: number, onExpired: () => void): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      onExpired();
      reject(new Error(`Payment init attempt timed out after ${timeoutMs}ms`));
    }, timeoutMs);

    task.then(resolve, reject).finally(() => clearTimeout(timer));
  });
}

export async function retryPaymentInit(task: PaymentInitTask, plan: RetryPlan): Promise<RetryResult> {
  const shouldContinue = () => plan.isAbandoned?.() !== true && plan.isOnline?.() !== false;

  let delayMs = plan.delayMs;
  let lastError: unknown;

  for (let attempt = 1; attempt <= plan.attempts; attempt++) {
    if (!shouldContinue()) {
      return { ok: false, error: lastError, attempts: attempt - 1 };
    }

    let isExpired = false;
    const isCurrent = () => !isExpired && plan.isAbandoned?.() !== true;

    try {
      await withDeadline(task(isCurrent), plan.attemptTimeoutMs, () => {
        isExpired = true;
      });

      return { ok: true, attempts: attempt };
    } catch (error: unknown) {
      lastError = error;
    }

    if (attempt === plan.attempts || !shouldContinue()) {
      return { ok: false, error: lastError, attempts: attempt };
    }

    plan.onRetry?.();
    await wait(delayMs);
    delayMs *= plan.delayFactor;

    if (!shouldContinue()) {
      return { ok: false, error: lastError, attempts: attempt };
    }
  }

  return { ok: false, error: lastError, attempts: plan.attempts };
}
